import React, {useMemo} from 'react';
import styled from 'styled-components';
import {t} from 'i18next';
import {Colors} from '@arboxappv4/shared/src/styles/Colors';
import {sortCourseEventDays, isSeriesEventFull, isSeriesEventBooked} from '../../../helpers/functions';
import TrackCourseEventRow from './TrackCourseEventRow';

const TrackCourseSection = ({course, selectedCourses, trackSessionPerWeek, onSelect}) => {
    const series = course.series;
    const staffName = useMemo(() => {
        const names = [series?.coach, series?.second_coach]
            .filter(Boolean)
            .map(staff => `${staff.first_name} ${staff.last_name || ''}`.trim());
        return names.join(', ');
    }, [series?.coach, series?.second_coach]);

    const sortedSeriesEvents = useMemo(
        () => sortCourseEventDays(series?.series_events ?? []),
        [series?.series_events]
    );

    const sectionTitle = useMemo(() => {
        return staffName ? `${series.series_name} - ${staffName}` : series.series_name;
    }, [series?.series_name, staffName]);

    const sessionLimitLabel = course.session_per_week != null
        ? t('track-course-session-limit', {count: course.session_per_week})
        : null;
        
    const isEventDisabled = (seriesEventId) => {
        const seriesEvent = (series?.series_events ?? []).find(event => event.id === seriesEventId);

        if (isSeriesEventFull(series, seriesEventId) || isSeriesEventBooked(series, seriesEventId)) {
            return true;
        }

        if (seriesEvent?.is_required) {
            return true;
        }
    
        const selectedIds = (selectedCourses ?? []).find(course => course.series_id === series.id)?.series_event_ids ?? [];
        if (selectedIds.includes(seriesEventId)) {
            return false;
        }
    
        const selectedCount = (selectedCourses ?? []).reduce((sum, course) => sum + (course.series_event_ids?.length ?? 0), 0);
        if (selectedCount >= trackSessionPerWeek) {
            return true;
        }
    
        if (course.session_per_week != null && selectedIds.length >= course.session_per_week) {
            return true;
        }
    
        return false;
    };    

    return (
        <SectionCard>
            <SectionHeader>
                <SectionTitle>{sectionTitle}</SectionTitle>
                {sessionLimitLabel && <SectionSubtitle>{sessionLimitLabel}</SectionSubtitle>}
            </SectionHeader>
            <EventsList>
                {sortedSeriesEvents.map(seriesEvent => (
                    <TrackCourseEventRow
                        key={seriesEvent.id}
                        seriesEvent={seriesEvent}
                        series={series}
                        onSelect={onSelect}
                        isDisabled={isEventDisabled(seriesEvent.id)}
                        isChecked={(selectedCourses ?? []).find(selectedCourse => selectedCourse.series_id === series.id)?.series_event_ids?.includes(seriesEvent.id) ?? false}
                    />
                ))}
            </EventsList>
        </SectionCard>
    );
};

export default TrackCourseSection;

const SectionCard = styled.div`
    background: ${Colors.white};
    border: 1px solid #e8e8e8;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    flex-shrink: 0;
    overflow: hidden;
`;

const SectionHeader = styled.div`
    padding: 12px 20px 13px;
    border-bottom: 1px solid #f0f0f0;
`;

const SectionTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: ${Colors.siteBlack};
    line-height: 20px;
`;

const SectionSubtitle = styled.div`
    font-size: 14px;
    font-weight: 400;
    color: ${Colors.greyText};
    line-height: 16px;
    padding-top: 2px;
`;

const EventsList = styled.div`
    display: flex;
    flex-direction: column;
`;
